import React, { useState, useEffect } from 'react'
import Layout from '../components/Layout'
import { templateAPI, photoAPI } from '../services/api'

const TrashPage = () => {
  const [activeTab, setActiveTab] = useState('templates')
  const [templates, setTemplates] = useState([])
  const [photos, setPhotos] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchTrashData()
  }, [])

  const fetchTrashData = async () => {
    try {
      setLoading(true)
      setError(null)

      // For now, we'll show empty state since soft delete isn't fully implemented
      // In a real implementation, you would call APIs like:
      // const templatesResponse = await templateAPI.getDeleted()
      // const photosResponse = await photoAPI.getDeleted()
      
      setTemplates([])
      setPhotos([])
    } catch (error) {
      console.error('Error fetching trash data:', error)
      setError('Failed to load trash data')
    } finally {
      setLoading(false)
    }
  }

  const handleRestore = async (type, id) => {
    try {
      if (type === 'template') {
        // await templateAPI.restore(id)
        alert(`Restoring template ${id}... (Feature in development)`)
      } else if (type === 'photo') {
        // await photoAPI.restore(id)
        alert(`Restoring photo ${id}... (Feature in development)`)
      }
      fetchTrashData()
    } catch (error) {
      console.error('Error restoring item:', error)
      alert('Failed to restore item')
    }
  }

  const handlePermanentDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to permanently delete this item? This action cannot be undone.')) {
      return
    }

    try {
      if (type === 'template') {
        // await templateAPI.permanentDelete(id)
        alert(`Permanently deleting template ${id}... (Feature in development)`)
      } else if (type === 'photo') {
        // await photoAPI.permanentDelete(id)
        alert(`Permanently deleting photo ${id}... (Feature in development)`)
      }
      fetchTrashData()
    } catch (error) {
      console.error('Error permanently deleting item:', error)
      alert('Failed to permanently delete item')
    }
  }

  const handleEmptyTrash = async () => {
    if (!window.confirm('Are you sure you want to empty the trash? This will permanently delete all items and cannot be undone.')) {
      return
    }

    try {
      // await trashAPI.emptyAll()
      alert('Emptying trash... (Feature in development)')
      fetchTrashData()
    } catch (error) {
      console.error('Error emptying trash:', error)
      alert('Failed to empty trash')
    }
  }

  const renderEmptyState = (type) => (
    <div className="text-center py-16">
      <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
        <svg className="w-12 h-12 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" clipRule="evenodd" />
          <path fillRule="evenodd" d="M10 5a2 2 0 00-2 2v6a2 2 0 002 2h4a2 2 0 002-2V7a2 2 0 00-2-2H10z" clipRule="evenodd" />
        </svg>
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">No deleted {type}</h3>
      <p className="text-gray-500">
        {type === 'templates' 
          ? 'Deleted templates will appear here. You can restore them or delete them permanently.'
          : 'Deleted photos will appear here. You can restore them or delete them permanently.'
        }
      </p>
    </div>
  )

  const renderTemplateCard = (template) => (
    <div key={template.id} className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="aspect-video bg-gray-200 relative">
        {template.thumbnail ? (
          <img
            src={template.thumbnail}
            alt={template.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            No Preview
          </div>
        )}
        <div className="absolute top-2 right-2">
          <span className="bg-red-500 text-white text-xs px-2 py-1 rounded">
            Deleted
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 mb-1">{template.name}</h3>
        <p className="text-sm text-gray-600 mb-3">{template.description}</p>
        <div className="flex space-x-2">
          <button
            onClick={() => handleRestore('template', template.id)}
            className="flex-1 bg-blue-600 text-white py-2 px-3 rounded text-sm hover:bg-blue-700 transition-colors"
          >
            Restore
          </button>
          <button
            onClick={() => handlePermanentDelete('template', template.id)}
            className="flex-1 bg-red-600 text-white py-2 px-3 rounded text-sm hover:bg-red-700 transition-colors"
          >
            Delete Forever
          </button>
        </div>
      </div>
    </div>
  )

  const renderPhotoCard = (photo) => (
    <div key={photo.id} className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="aspect-square bg-gray-200 relative">
        {photo.url ? (
          <img
            src={photo.url}
            alt={photo.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            No Preview
          </div>
        )}
        <div className="absolute top-2 right-2">
          <span className="bg-red-500 text-white text-xs px-2 py-1 rounded">
            Deleted
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 mb-1">{photo.name}</h3>
        <p className="text-sm text-gray-600 mb-3">{photo.description}</p>
        <div className="flex space-x-2">
          <button
            onClick={() => handleRestore('photo', photo.id)}
            className="flex-1 bg-blue-600 text-white py-2 px-3 rounded text-sm hover:bg-blue-700 transition-colors"
          >
            Restore
          </button>
          <button
            onClick={() => handlePermanentDelete('photo', photo.id)}
            className="flex-1 bg-red-600 text-white py-2 px-3 rounded text-sm hover:bg-red-700 transition-colors"
          >
            Delete Forever
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Trash</h1>
              <p className="text-gray-600">
                Manage deleted items. Items in trash will be permanently deleted after 30 days.
              </p>
            </div>
            <button
              onClick={handleEmptyTrash}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" clipRule="evenodd" />
                <path fillRule="evenodd" d="M10 5a2 2 0 00-2 2v6a2 2 0 002 2h4a2 2 0 002-2V7a2 2 0 00-2-2H10z" clipRule="evenodd" />
              </svg>
              <span>Empty Trash</span>
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-8">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('templates')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'templates'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Templates ({templates.length})
              </button>
              <button
                onClick={() => setActiveTab('photos')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'photos'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Photos ({photos.length})
              </button>
            </nav>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-12 h-12 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error loading trash</h3>
              <p className="text-gray-500 mb-4">{error}</p>
              <button
                onClick={fetchTrashData}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : (
            <div>
              {activeTab === 'templates' && (
                <div>
                  {templates.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {templates.map(renderTemplateCard)}
                    </div>
                  ) : (
                    renderEmptyState('templates')
                  )}
                </div>
              )}

              {activeTab === 'photos' && (
                <div>
                  {photos.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                      {photos.map(renderPhotoCard)}
                    </div>
                  ) : (
                    renderEmptyState('photos')
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  )
}

export default TrashPage
